import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import PLPortfolio from './PLPortfolio';
import LifetimeStats from './LifetimeStats';
import { fmtIST } from './dashboardUtils';

const API = 'https://aaktradex.in';

const C = {
  green: '#3B6D11', lgreen: '#EAF3DE', bgreen: '#C0DD97',
  amber: '#854F0B', lamber: '#FAEEDA', bamber: '#FAC775',
  red:   '#A32D2D', lred:   '#FCEBEB', bred:   '#F7C1C1',
  blue:  '#185FA5', lblue:  '#E6F1FB', bblue:  '#B5D4F4',
  purple:'#6B21A8', lpurple:'#F3E8FF', bpurple:'#C4B5FD',
  gray:  '#6B7280', lgray:  '#F3F4F6', bgray:  '#D1D5DB',
};

function Section({ title, children, right }) {
  return (
    <div style={{
      background: 'var(--bg1)',
      border: '1px solid var(--border2)',
      borderRadius: 10,
      padding: 16,
      marginBottom: 16,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        marginBottom: 12, alignItems: 'center',
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--t1)' }}>{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function CondPill({ label, ok }) {
  const bg = ok ? C.lgreen : C.lred;
  const color = ok ? C.green : C.red;
  const border = ok ? C.bgreen : C.bred;
  return (
    <span style={{
      fontSize: 9, fontWeight: 500, padding: '2px 6px',
      borderRadius: 8, background: bg, color, border: `0.5px solid ${border}`,
      marginRight: 4, marginBottom: 4, display: 'inline-block',
    }}>{ok ? '✓' : '✗'} {label}</span>
  );
}

// ========================================
// Watchlist Section
// ========================================

function WatchlistSection({ watchlist, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [startDate, setStartDate] = useState(
    watchlist?.cycle_start_date || new Date().toISOString().slice(0, 10)
  );
  const [endDate, setEndDate] = useState(watchlist?.cycle_end_date || '');
  const [tickers, setTickers] = useState(
    // 2026-08-10: was Array(10 - tickers.length), which throws
    // "Invalid array length" once the basket exceeds 10 names. The 10 Aug
    // rebalance holds 11 plus LIQUIDBEES, so the model is no longer fixed at
    // ten. Pad up to 10 for the edit form, never negative.
    watchlist?.tickers?.length
      ? [...watchlist.tickers, ...Array(Math.max(0, 10 - watchlist.tickers.length)).fill('')]
      : Array(10).fill('')
  );
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const clean = tickers.map(t => t.trim().toUpperCase()).filter(Boolean);
    if (clean.length === 0) { alert('Enter at least one ticker'); return; }
    setSaving(true);
    try {
      const payload = { cycle_start_date: startDate, tickers: clean };
      if (endDate) payload.cycle_end_date = endDate;
      const res = await axios.post(`${API}/trailb/watchlist`, payload);
      alert(`Saved ${res.data.count} stocks for cycle ${res.data.cycle_start_date} to ${res.data.cycle_end_date}`);
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (!editing && watchlist?.tickers?.length) {
    return (
      <Section
        title="Watchlist"
        right={
          <button
            onClick={() => setEditing(true)}
            style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 6,
              background: C.lblue, color: C.blue, border: `0.5px solid ${C.bblue}`,
              cursor: 'pointer', fontWeight: 500,
            }}
          >Edit</button>
        }
      >
        <div style={{ fontSize: 11, color: 'var(--t2)', marginBottom: 8 }}>
          Cycle: {watchlist.cycle_start_date} to {watchlist.cycle_end_date}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {watchlist.tickers.map((t, i) => (
            <span key={i} style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 6,
              background: C.lpurple, color: C.purple, border: `0.5px solid ${C.bpurple}`,
              fontWeight: 500,
            }}>{t}</span>
          ))}
        </div>
      </Section>
    );
  }

  return (
    <Section title={editing ? 'Edit Watchlist' : 'Enter Watchlist (10 stocks)'}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div>
          <label style={{ fontSize: 10, color: 'var(--t2)', display: 'block' }}>Cycle Start</label>
          <input
            type="date" value={startDate}
            onChange={e => setStartDate(e.target.value)}
            style={{ fontSize: 12, padding: 4, border: `1px solid ${C.bgray}`, borderRadius: 4 }}
          />
        </div>
        <div>
          <label style={{ fontSize: 10, color: 'var(--t2)', display: 'block' }}>Cycle End (auto if blank)</label>
          <input
            type="date" value={endDate}
            onChange={e => setEndDate(e.target.value)}
            style={{ fontSize: 12, padding: 4, border: `1px solid ${C.bgray}`, borderRadius: 4 }}
          />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 12 }}>
        {tickers.map((t, i) => (
          <input
            key={i}
            type="text"
            value={t}
            placeholder={`Stock ${i + 1}`}
            onChange={e => {
              const next = [...tickers];
              next[i] = e.target.value.toUpperCase();
              setTickers(next);
            }}
            style={{
              fontSize: 12, padding: 6, border: `1px solid ${C.bgray}`, borderRadius: 4,
              fontWeight: 500,
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={save} disabled={saving}
          style={{
            fontSize: 12, padding: '6px 14px', borderRadius: 6,
            background: C.green, color: '#fff', border: 'none',
            cursor: 'pointer', fontWeight: 500,
          }}
        >{saving ? 'Saving...' : 'Save Watchlist'}</button>
        {editing && (
          <button
            onClick={() => setEditing(false)}
            style={{
              fontSize: 12, padding: '6px 14px', borderRadius: 6,
              background: 'transparent', color: 'var(--t2)',
              border: `1px solid ${C.bgray}`, cursor: 'pointer',
            }}
          >Cancel</button>
        )}
      </div>
    </Section>
  );
}

// ========================================
// Live Status Section
// ========================================

const CONDITION_LABELS = {
  multi_day_trend: 'Trend',
  green_day: 'Green',
  ema_stack: 'EMA',
  macd_bullish: 'MACD',
  rsi_in_band: 'RSI',
  timing_ok: 'Time',
  nifty_ok: 'Nifty',
};

function StatusSection({ status }) {
  if (!status?.watchlist_active) {
    return (
      <Section title="Live Setup Status">
        <div style={{ fontSize: 11, color: 'var(--t2)' }}>No active watchlist</div>
      </Section>
    );
  }
  return (
    <Section
      title="Live Setup Status"
      right={
        <div style={{ fontSize: 10, color: 'var(--t2)' }}>
          Nifty {status.nifty_change_pct >= 0 ? '+' : ''}{status.nifty_change_pct?.toFixed(2)}%
          · {status.open_positions_count}/{status.max_positions} positions
        </div>
      }
    >
      <div style={{ overflow: 'auto' }}>
        <table style={{ fontSize: 11, width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.bgray}` }}>
              <th style={{ textAlign: 'left', padding: 6 }}>Ticker</th>
              <th style={{ textAlign: 'right', padding: 6 }}>LTP</th>
              <th style={{ textAlign: 'right', padding: 6 }}>Day%</th>
              <th style={{ textAlign: 'left', padding: 6 }}>Conditions (7)</th>
              <th style={{ textAlign: 'center', padding: 6 }}>Ready</th>
            </tr>
          </thead>
          <tbody>
            {status.stocks?.map(s => (
              <tr key={s.ticker} style={{ borderBottom: `0.5px solid ${C.lgray}` }}>
                <td style={{ padding: 6, fontWeight: 600 }}>{s.ticker}</td>
                <td style={{ padding: 6, textAlign: 'right' }}>{s.ltp?.toFixed(2)}</td>
                <td style={{ padding: 6, textAlign: 'right',
                  color: s.day_change_pct > 0 ? C.green : s.day_change_pct < 0 ? C.red : C.gray,
                  fontWeight: 500,
                }}>
                  {s.day_change_pct >= 0 ? '+' : ''}{s.day_change_pct?.toFixed(2)}%
                </td>
                <td style={{ padding: 6 }}>
                  {s.conditions && Object.entries(s.conditions).map(([k, v]) => (
                    <CondPill key={k} label={CONDITION_LABELS[k] || k} ok={v} />
                  ))}
                </td>
                <td style={{ padding: 6, textAlign: 'center' }}>
                  {s.already_in_position ? (
                    <span style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 4,
                      background: C.lblue, color: C.blue, fontWeight: 600,
                    }}>IN POSITION</span>
                  ) : s.all_conditions_met ? (
                    <span style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 4,
                      background: C.lgreen, color: C.green, fontWeight: 600,
                    }}>READY</span>
                  ) : (
                    <span style={{ fontSize: 10, color: 'var(--t3)' }}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ========================================
// Positions Section
// ========================================

function PositionsSection({ positions }) {
  const open = positions?.open || [];
  const closed = positions?.closed || [];
  return (
    <Section title={`Positions (${open.length} open, ${closed.length} closed)`}>
      {open.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, color: C.blue }}>Open</div>
          <table style={{ fontSize: 11, width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.bgray}` }}>
                <th style={{ textAlign: 'left', padding: 6 }}>Ticker</th>
                <th style={{ textAlign: 'right', padding: 6 }}>Entry</th>
                <th style={{ textAlign: 'right', padding: 6 }}>LTP</th>
                <th style={{ textAlign: 'right', padding: 6 }}>Qty</th>
                <th style={{ textAlign: 'right', padding: 6 }}>SL</th>
                <th style={{ textAlign: 'right', padding: 6 }}>SL%</th>
                <th style={{ textAlign: 'right', padding: 6 }}>Risk</th>
                <th style={{ textAlign: 'right', padding: 6 }}>Unrealized (net)</th>
                <th style={{ textAlign: 'right', padding: 6 }}>P&L/Risk</th>
                <th style={{ textAlign: 'right', padding: 6 }}>MFE%</th>
                <th style={{ textAlign: 'left', padding: 6 }}>Entered</th>
              </tr>
            </thead>
            <tbody>
              {open.map(p => {
                const slPct = p.sl_pct_from_ltp;
                // Warn color if stop is within 1% of LTP
                const slPctColor = slPct != null && slPct < 1 ? C.red : slPct != null && slPct < 1.5 ? C.amber : C.gray;
                return (
                <tr key={p.id} style={{ borderBottom: `0.5px solid ${C.lgray}` }}>
                  <td style={{ padding: 6, fontWeight: 600 }}>{p.ticker}</td>
                  <td style={{ padding: 6, textAlign: 'right' }}>{p.entry_price?.toFixed(2)}</td>
                  <td style={{ padding: 6, textAlign: 'right' }}>{p.ltp?.toFixed(2) || '—'}</td>
                  <td style={{ padding: 6, textAlign: 'right' }}>{p.qty}</td>
                  <td style={{ padding: 6, textAlign: 'right', color: C.red }}>{p.stop_loss?.toFixed(2)}</td>
                  <td style={{ padding: 6, textAlign: 'right', color: slPctColor, fontWeight: 500 }}>
                    {slPct != null ? `${slPct.toFixed(2)}%` : '—'}
                  </td>
                  <td style={{ padding: 6, textAlign: 'right', color: C.red }}>
                    {p.risk_rs != null ? `Rs${p.risk_rs.toFixed(0)}` : '—'}
                  </td>
                  <td style={{ padding: 6, textAlign: 'right', fontWeight: 600,
                    color: (p.unrealized_net ?? p.unrealized_gross ?? 0) >= 0 ? C.green : C.red,
                  }}>
                    {p.unrealized_net != null
                      ? `${p.unrealized_net >= 0 ? '+' : ''}Rs${p.unrealized_net.toFixed(0)} (${p.unrealized_net_pct >= 0 ? '+' : ''}${p.unrealized_net_pct?.toFixed(2)}%)`
                      : (p.unrealized_gross != null
                          ? `${p.unrealized_gross >= 0 ? '+' : ''}Rs${p.unrealized_gross.toFixed(0)} (${p.unrealized_pct >= 0 ? '+' : ''}${p.unrealized_pct?.toFixed(2)}%)`
                          : '—')}
                  </td>
                  <td style={{ padding: 6, textAlign: 'right', fontWeight: 600,
                    color: (p.unrealized_over_risk || 0) >= 0 ? C.green : C.red,
                  }}>
                    {p.unrealized_over_risk != null
                      ? `${p.unrealized_over_risk >= 0 ? '+' : ''}${p.unrealized_over_risk.toFixed(2)}x`
                      : '—'}
                  </td>
                  <td style={{ padding: 6, textAlign: 'right' }}>
                    {p.mfe_pct != null ? `${p.mfe_pct.toFixed(2)}%` : '—'}
                  </td>
                  <td style={{ padding: 6, textAlign: 'center', fontSize: 9,
                    fontWeight: p.current_stage === 'HALF_BOOKED' ? 700 : 400,
                    color: p.current_stage === 'HALF_BOOKED' ? C.green : 'var(--t3)' }}>
                    {p.current_stage === 'HALF_BOOKED' ? '½ BOOKED' : (p.current_stage || 'INITIAL')}
                  </td>
                  <td style={{ padding: 6, fontSize: 10, color: 'var(--t3)' }}>
                    {p.entry_ts ? new Date(p.entry_ts + 'Z').toLocaleString('en-GB', {
                      day: '2-digit', month: '2-digit', year: '2-digit',
                      hour: '2-digit', minute: '2-digit',
                      timeZone: 'Asia/Kolkata', hour12: false,
                    }).replace(',', '') + ' IST' : '—'}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </>
      )}
      {closed.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, color: 'var(--t2)' }}>Closed</div>
          <table style={{ fontSize: 11, width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.bgray}` }}>
                <th style={{ textAlign: 'left', padding: 6 }}>Ticker</th>
                <th style={{ textAlign: 'right', padding: 6 }}>Entry</th>
                <th style={{ textAlign: 'right', padding: 6 }}>Exit</th>
                <th style={{ textAlign: 'left', padding: 6 }}>Reason</th>
                <th style={{ textAlign: 'right', padding: 6 }}>Net P&L</th>
                <th style={{ textAlign: 'right', padding: 6 }}>MFE%</th>
                <th style={{ textAlign: 'left', padding: 6 }}>Entered</th>
                <th style={{ textAlign: 'left', padding: 6 }}>Exited</th>
              </tr>
            </thead>
            <tbody>
              {closed.map(p => (
                <tr key={p.id} style={{ borderBottom: `0.5px solid ${C.lgray}` }}>
                  <td style={{ padding: 6, fontWeight: 600 }}>{p.ticker}</td>
                  <td style={{ padding: 6, textAlign: 'right' }}>{p.entry_price?.toFixed(2)}</td>
                  <td style={{ padding: 6, textAlign: 'right' }}>{p.exit_price?.toFixed(2)}</td>
                  <td style={{ padding: 6, fontSize: 10 }}>{p.exit_reason}</td>
                  <td style={{ padding: 6, textAlign: 'right', fontWeight: 600,
                    color: (p.net_pnl || 0) >= 0 ? C.green : C.red,
                  }}>
                    {p.net_pnl >= 0 ? '+' : ''}Rs{p.net_pnl?.toFixed(0)}
                  </td>
                  <td style={{ padding: 6, textAlign: 'right' }}>
                    {p.mfe_pct != null ? `${p.mfe_pct.toFixed(2)}%` : '—'}
                  </td>
                  <td style={{ padding: 6, fontSize: 10, color: 'var(--t3)' }}>
                    {p.entry_ts ? fmtIST(p.entry_ts) : '—'}
                  </td>
                  <td style={{ padding: 6, fontSize: 10, color: 'var(--t3)' }}>
                    {p.exit_ts ? fmtIST(p.exit_ts) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {open.length === 0 && closed.length === 0 && (
        <div style={{ fontSize: 11, color: 'var(--t2)' }}>No positions yet.</div>
      )}
    </Section>
  );
}

// ========================================
// Summary Section
// ========================================

function SummarySection({ summary }) {
  if (!summary || summary.error) return null;
  return (
    <Section title="Cycle Summary">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
        {[
          ['Trades', summary.total_trades],
          ['Open', summary.open_trades],
          ['Closed', summary.closed_trades],
          ['Wins', summary.wins],
          ['Losses', summary.losses],
          ['Win Rate', `${summary.win_rate_pct}%`],
          ['Net P&L', `Rs${summary.total_net_pnl?.toFixed(0)}`],
          ['Avg/Trade', `Rs${summary.avg_net_pnl_per_trade?.toFixed(0)}`],
        ].map(([label, val]) => (
          <div key={label} style={{ padding: 8, background: 'var(--bg2)', borderRadius: 6 }}>
            <div style={{ fontSize: 10, color: 'var(--t2)' }}>{label}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--t1)' }}>{val}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ========================================
// Main TrailBActive component
// ========================================

export default function TrailBActive() {
  const [watchlist, setWatchlist] = useState(null);
  const [status, setStatus] = useState(null);
  const [positions, setPositions] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [w, s, p, sum] = await Promise.all([
        axios.get(`${API}/trailb/watchlist`).catch(() => ({ data: null })),
        axios.get(`${API}/trailb/status`).catch(() => ({ data: null })),
        axios.get(`${API}/trailb/positions`).catch(() => ({ data: null })),
        axios.get(`${API}/trailb/summary`).catch(() => ({ data: null })),
      ]);
      setWatchlist(w.data);
      setStatus(s.data);
      setPositions(p.data);
      setSummary(sum.data);
    } catch (e) {
      console.error('TrailB fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => clearInterval(t);
  }, [fetchAll]);

  if (loading) return <div style={{ padding: 20 }}>Loading TrailB Active...</div>;

  return (
    <div style={{ padding: 16 }}>
      <LifetimeStats />
      <div style={{
        marginBottom: 16, padding: 12,
        background: C.lpurple, color: C.purple,
        border: `0.5px solid ${C.bpurple}`, borderRadius: 8,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>TrailB Active v1.0</div>
        <div style={{ fontSize: 11, marginTop: 2 }}>
          Short-swing overlay on PL Trailblazers watchlist · Paper mode ·
          Rs1.25L/position · Max 4 positions · 2% stop · 7-day time cap
        </div>
        {positions?.capital && (
          <div style={{
            marginTop: 10, display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8,
          }}>
            <div><div style={{ fontSize: 9, opacity: 0.7 }}>TOTAL CAPITAL</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Rs{(positions.capital.total/100000).toFixed(2)}L</div></div>
            <div><div style={{ fontSize: 9, opacity: 0.7 }}>DEPLOYED</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Rs{(positions.capital.deployed/100000).toFixed(2)}L</div></div>
            <div><div style={{ fontSize: 9, opacity: 0.7 }}>AVAILABLE</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Rs{(positions.capital.available/100000).toFixed(2)}L</div></div>
            <div><div style={{ fontSize: 9, opacity: 0.7 }}>SLOTS</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{positions.capital.used_positions}/{positions.capital.max_positions}</div></div>
            <div><div style={{ fontSize: 9, opacity: 0.7 }}>UNREALIZED P&L</div>
              <div style={{ fontSize: 14, fontWeight: 700,
                color: positions.capital.unrealized_pnl >= 0 ? C.green : C.red }}>
                {positions.capital.unrealized_pnl >= 0 ? '+' : ''}Rs{positions.capital.unrealized_pnl?.toFixed(0)}
              </div></div>
          </div>
        )}
      </div>

      <PLPortfolio />

      <WatchlistSection watchlist={watchlist} onSaved={fetchAll} />
      <StatusSection status={status} />
      <PositionsSection positions={positions} />
      <SummarySection summary={summary} />
    </div>
  );
}
